// ============================================
// FILE: /frontend/src/pages/PromoteCheckoutPage.jsx
// PURPOSE: One-page checkout for listing promotion with phone input
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { dashboardAPI } from '../api/dashboard.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Star, ArrowLeft, Phone, CheckCircle, Calendar, Zap } from 'lucide-react';

export default function PromoteCheckoutPage() {
  console.log('[PromoteCheckout] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [listing, setListing] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    // Get promotion details from navigation state
    const promotionData = location.state;

    if (!promotionData?.listing || !promotionData?.plan) {
      console.log('[PromoteCheckout] Missing promotion data, redirecting');
      toast({
        variant: 'destructive',
        title: 'No Promotion Data',
        description: 'Please select a plan to promote your listing.',
      });
      navigate('/my-listings');
      return;
    }

    console.log('[PromoteCheckout] Promotion data loaded:', promotionData);
    setListing(promotionData.listing);
    setSelectedPlan(promotionData.plan);

    // Pre-fill phone number from user profile
    if (user?.phone) {
      setPhoneNumber(user.phone);
    }
  }, [location.state, user, navigate, toast]);

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^254[17]\d{8}$/;
    
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (!phoneRegex.test(phone)) {
      setPhoneError('Invalid format. Use 254XXXXXXXXX (e.g., 254712345678)');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    if (value) {
      validatePhoneNumber(value);
    }
  };

  const handleProceedToPayment = async () => {
    console.log('[PromoteCheckout] Proceeding to payment');

    if (!validatePhoneNumber(phoneNumber)) {
      return;
    }

    if (!selectedPlan || !listing) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Missing promotion details.',
      });
      return;
    }

    setLoading(true);

    try {
      console.log('[PromoteCheckout] Initiating promotion payment:', {
        listingId: listing.id,
        duration: selectedPlan.duration,
        phoneNumber,
      });

      const response = await dashboardAPI.promoteToFeatured({
        listingId: listing.id,
        duration: selectedPlan.duration,
        phoneNumber: phoneNumber,
      });

      console.log('[PromoteCheckout] Payment initiated:', response);

      if (response.success) {
        // Store payment details for status page
        sessionStorage.setItem('paymentDetails', JSON.stringify({
          paymentId: response.paymentId,
          checkoutRequestId: response.checkoutRequestId,
          tier: `FEATURED_${selectedPlan.duration}D`,
          amount: selectedPlan.price,
          phoneNumber: phoneNumber,
          type: 'PROMOTION',
          listingId: listing.id,
          listingTitle: listing.title,
        }));

        toast({
          title: 'Payment Initiated',
          description: 'Please check your phone for the M-Pesa prompt.',
        });

        // Navigate to payment status page
        navigate('/subscription/payment-status', {
          state: {
            paymentId: response.paymentId,
            checkoutRequestId: response.checkoutRequestId,
            tier: `FEATURED_${selectedPlan.duration}D`,
            amount: selectedPlan.price,
            phoneNumber: phoneNumber,
            type: 'PROMOTION',
            listingId: listing.id,
          },
        });
      }
    } catch (error) {
      console.error('[PromoteCheckout] Payment error:', error);
      toast({
        variant: 'destructive',
        title: 'Payment Failed',
        description: error.response?.data?.message || 'Failed to initiate payment. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!selectedPlan || !listing) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-12 md:py-20">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/my-listings')}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to My Listings
          </Button>

          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold flex items-center justify-center gap-2">
                <Star className="w-8 h-8 text-yellow-500" />
                Promote Your Listing
              </CardTitle>
              <CardDescription>
                Review your selection and enter your M-Pesa number
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Listing Info */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Promoting:</p>
                <h3 className="text-lg font-bold">{listing.title}</h3>
                <p className="text-sm text-muted-foreground capitalize">
                  {listing.make} {listing.model} • {listing.year}
                </p>
              </div>

              {/* Plan Summary */}
              <div className="bg-primary/5 p-6 rounded-lg border-2 border-primary/20">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Selected Plan</p>
                    <h3 className="text-2xl font-bold flex items-center gap-2">
                      <Calendar className="w-6 h-6 text-primary" />
                      {selectedPlan.label}
                    </h3>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary">
                      KES {selectedPlan.price.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ~KES {Math.round(selectedPlan.price / selectedPlan.duration)}/day
                    </p>
                  </div>
                </div>

                {selectedPlan.popular && (
                  <div className="flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-md text-sm font-semibold">
                    <Zap className="w-4 h-4" />
                    Most Popular Choice
                  </div>
                )}
              </div>

              {/* Benefits */}
              <div className="border rounded-lg p-4">
                <p className="text-sm font-semibold mb-3">What you'll get:</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">Appear at the top of search results</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">Get highlighted with a featured badge</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">Increase visibility by up to 10x</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">Stand out from regular listings</span>
                  </div>
                </div>
              </div>

              {/* Phone Number Input */}
              <div className="space-y-2">
                <Label htmlFor="phone">M-Pesa Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="254712345678"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className={`pl-10 ${phoneError ? 'border-red-500' : ''}`}
                    maxLength={12}
                  />
                </div>
                {phoneError && (
                  <p className="text-sm text-red-500">{phoneError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Enter the phone number registered with M-Pesa. Format: 254XXXXXXXXX
                </p>
              </div>

              {/* Important Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Important:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• You will receive an M-Pesa prompt on your phone</li>
                  <li>• Enter your M-Pesa PIN to complete payment</li>
                  <li>• Your listing will be promoted immediately after payment</li>
                  <li>• Promotion is valid for {selectedPlan.duration} days</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter>
              <Button
                size="lg"
                className="w-full"
                onClick={handleProceedToPayment}
                disabled={loading || !phoneNumber || !!phoneError}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Star className="mr-2 h-4 w-4" />
                    Pay KES {selectedPlan.price.toLocaleString()}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}